// Profile Dropdown Menu
import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  User, 
  Settings, 
  LogOut, 
  CreditCard, 
  HelpCircle,
  ChevronDown 
} from 'lucide-react';
import { useAuthStore } from '@stores/authStore';
import { useStableAuthValues } from './StableAuthValues';
import { useToast } from '@components/ui/Toast';
import { ROUTES } from '@config/constants';
import { UserPlan } from '@types';

export const ProfileDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { planReady } = useStableAuthValues();
  const navigate = useNavigate();
  const toast = useToast();
  
  // CRITICAL: Stabilize user state to prevent flicker on tab switch
  // Keep the last known user state even if it briefly becomes null
  // Do NOT write to sessionStorage here – nur der Auth-Store soll fyle-user-state schreiben (und nur mit fullUser aus DB), sonst flackert der Username
  const stableUser = useMemo(() => {
    if (user) return user;
    if (typeof window !== 'undefined') {
      try {
        const storedUser = sessionStorage.getItem('fyle-user-state');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          return parsed;
        }
      } catch (e) {}
    }
    return null;
  }, [user]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        // Check if click is outside both button AND menu
        if (
          buttonRef.current && 
          !buttonRef.current.contains(target) &&
          menuRef.current &&
          !menuRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };
      
      // Use a small delay to prevent immediate closing
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('🚪 Logging out...');
    setIsLoggingOut(true);
    setIsOpen(false); // Close menu immediately
    
    try {
      await logout();
      // Navigation is handled by authStore.signOut()
      console.log('✅ Logout successful');
      toast.success('Logged out', 'You have been successfully logged out.');
    } catch (error) {
      console.error('❌ Logout failed:', error);
      toast.error('Logout failed', 'An error occurred while logging out. Please try again.');
      setIsLoggingOut(false);
    }
  };

  if (!stableUser) return null;

  // Always show a stable fallback name to avoid skeleton flicker:
  // username -> email prefix -> "User".
  const displayName =
    (stableUser.username && stableUser.username.trim()) ||
    stableUser.email?.split('@')[0] ||
    'User';

  const menuItems = [
    {
      icon: User,
      label: 'My Profile',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('My Profile clicked');
        setIsOpen(false);
        navigate('/settings');
      },
    },
    {
      icon: Settings,
      label: 'Settings',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('Settings clicked');
        setIsOpen(false);
        navigate('/settings');
      },
    },
    {
      icon: CreditCard,
      label: 'Billing',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('Billing clicked');
        setIsOpen(false);
        navigate(ROUTES.BILLING);
      },
      badge: !planReady ? '' : stableUser.plan === UserPlan.FREE ? 'Free' : stableUser.plan.toUpperCase(),
    },
    {
      icon: HelpCircle,
      label: 'Help & Support',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('Help clicked');
        setIsOpen(false);
        navigate(ROUTES.SUPPORT);
      },
    },
  ];

  return (
    <div className="relative">
      {/* Profile Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-all group"
      >
        {/* User Icon */}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
          <User className="w-5 h-5 text-primary" />
        </div>

        {/* User Info */}
        <div className="hidden md:block text-left">
          <p className="font-inter text-sm font-semibold text-white">
            {displayName}
          </p>
          <p className="font-inter text-xs text-white/40">
            {!planReady ? (
              <span className="inline-block w-16 h-3 rounded bg-white/10 animate-pulse" aria-hidden />
            ) : (
              <>{stableUser.plan === UserPlan.FREE ? 'Free Plan' : `${stableUser.plan.toUpperCase()} Plan`}</>
            )}
          </p>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown 
          className={`w-4 h-4 text-white/40 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu (Portal) */}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[101] w-64 bg-dark-secondary/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-slide-in"
            style={{ 
              top: menuPosition.top, 
              right: menuPosition.right 
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
              {/* User Info Header */}
              <div className="p-4 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-inter text-sm font-semibold text-white truncate">
                      {displayName}
                    </p>
                    <p className="font-inter text-xs text-white/50 truncate">
                      {stableUser.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                {menuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors group"
                  >
                    <item.icon className="w-4.5 h-4.5 text-white/50 group-hover:text-primary transition-colors" />
                    <span className="flex-1 text-left font-inter text-sm text-white/80 group-hover:text-white transition-colors">
                      {item.label}
                    </span>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-inter font-semibold rounded">
                        {item.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div className="h-px bg-white/10" />

              {/* Logout */}
              <div className="p-2">
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-500/10 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoggingOut ? (
                    <>
                      <div className="relative flex items-center justify-center">
                        {/* Outer pulsing ring */}
                        <div className="absolute w-4.5 h-4.5 border-2 border-red-400/20 rounded-full animate-pulse-ring" />
                        {/* Spinning ring */}
                        <div className="w-4.5 h-4.5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin-slow" />
                        {/* Inner pulse */}
                        <div className="absolute w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse opacity-70" />
                      </div>
                      <span className="font-inter text-sm text-red-400 animate-pulse">
                        Logging out...
                      </span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4.5 h-4.5 text-white/50 group-hover:text-red-400 transition-colors" />
                      <span className="font-inter text-sm text-white/80 group-hover:text-red-400 transition-colors">
                        Logout
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>,
          document.body
        )}
    </div>
  );
};

